// Iter · Feb 2026 — Timeline AI Insights.
// Heuristic-first panel that flags Monday-standup-worthy risks so a PM
// can spot trouble without eyeballing the timeline. All flags are
// computed client-side (no cost, no latency). An optional "Ask AI"
// button hands the flags to a backend LLM narrative call for a Slack-
// ready summary.
//
// Heuristics we currently run:
//   • Overdue clusters — days where 3+ items are past today's date
//     and not yet published.
//   • Assignee overload — assignees with 8+ pending tasks in the
//     visible month.
//   • At-risk brands — brands where >30% of items in the final 7 days
//     of the month are still pending.
//   • Empty week gaps — 7-day windows with zero scheduled
//     deliverables (a red flag for content velocity).

import React, { useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, AlertTriangle, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const SEVERITY_META = {
  high:   { dot: "bg-rose-500",  text: "text-rose-600",  label: "High" },
  medium: { dot: "bg-amber-500", text: "text-amber-700", label: "Medium" },
  low:    { dot: "bg-sky-500",   text: "text-sky-700",   label: "Low" },
};

function toDateStr(y, m, d) {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export default function TimelineInsights({ items, year, month, wsId, workspaceName, onFilterChange }) {
  const [expanded, setExpanded] = useState(true);
  const [loadingAI, setLoadingAI] = useState(false);
  const [narrative, setNarrative] = useState("");

  const heuristics = useMemo(() => {
    const found = [];
    const today = new Date().toISOString().slice(0, 10);
    const daysInMonth = new Date(year, month, 0).getDate();

    // 1) Overdue clusters — day → items
    const overdueByDay = new Map();
    for (const it of items || []) {
      if (!it.planned_date) continue;
      if (it.planned_date >= today) continue;
      if (it.status === "published" || it.status === "cancelled") continue;
      const arr = overdueByDay.get(it.planned_date) || [];
      arr.push(it);
      overdueByDay.set(it.planned_date, arr);
    }
    for (const [day, arr] of overdueByDay) {
      if (arr.length < 3) continue;
      const severity = arr.length >= 5 ? "high" : "medium";
      found.push({
        kind: "overdue_cluster",
        severity,
        label: `${arr.length} overdue on ${day}`,
        detail: arr.slice(0, 4).map((x) => x.name).join(", ") + (arr.length > 4 ? ` +${arr.length - 4}` : ""),
        itemIds: arr.map((x) => x.id),
      });
    }

    // 2) Assignee overload — assignee → pending task count
    const assigneeMap = new Map();
    const assigneeItems = new Map(); // assigneeName -> Set(itemIds)
    for (const it of items || []) {
      if (it.status === "published" || it.status === "cancelled") continue;
      for (const t of (it.tasks || [])) {
        if (t.status === "done" || t.status === "cancelled") continue;
        const name = t.assignee_name || "Unassigned";
        assigneeMap.set(name, (assigneeMap.get(name) || 0) + 1);
        if (!assigneeItems.has(name)) assigneeItems.set(name, new Set());
        assigneeItems.get(name).add(it.id);
      }
    }
    for (const [name, count] of assigneeMap) {
      if (count < 8) continue;
      const severity = count >= 15 ? "high" : "medium";
      found.push({
        kind: "assignee_overload",
        severity,
        label: `${name} has ${count} pending tasks`,
        detail: name === "Unassigned"
          ? "Assign owners so the work doesn't slip further."
          : `Consider re-balancing to teammates with lighter load.`,
        itemIds: [...(assigneeItems.get(name) || [])],
      });
    }

    // 3) At-risk brands — % pending in final 7 days of the month
    const cutoffDay = Math.max(1, daysInMonth - 6);
    const cutoff = toDateStr(year, month, cutoffDay);
    const brandStat = new Map();
    const brandItems = new Map();
    for (const it of items || []) {
      if (!it.planned_date) continue;
      if (it.planned_date < cutoff) continue;
      if (it.status === "cancelled") continue;
      const key = it.brand_name || "—";
      const s = brandStat.get(key) || { total: 0, pending: 0 };
      s.total += 1;
      if (it.status !== "published") s.pending += 1;
      brandStat.set(key, s);
      if (!brandItems.has(key)) brandItems.set(key, []);
      if (it.status !== "published") brandItems.get(key).push(it.id);
    }
    for (const [name, s] of brandStat) {
      if (s.total < 3) continue;
      const pct = s.pending / s.total;
      if (pct < 0.3) continue;
      const severity = pct >= 0.5 ? "high" : "medium";
      found.push({
        kind: "at_risk_brand",
        severity,
        label: `${name}: ${s.pending}/${s.total} still pending in last week`,
        detail: `${Math.round(pct * 100)}% pending in final 7 days — accelerate reviews.`,
        itemIds: brandItems.get(name) || [],
      });
    }

    // 4) Empty week gaps
    const daysWithItems = new Set(
      (items || []).map((it) => it.planned_date).filter(Boolean)
    );
    let runStart = null, longestRun = 0, longestStart = null;
    for (let d = 1; d <= daysInMonth; d++) {
      const day = toDateStr(year, month, d);
      if (!daysWithItems.has(day)) {
        if (runStart === null) runStart = d;
        const run = d - runStart + 1;
        if (run > longestRun) { longestRun = run; longestStart = runStart; }
      } else {
        runStart = null;
      }
    }
    if (longestRun >= 7) {
      const severity = longestRun >= 10 ? "high" : "medium";
      const fromStr = toDateStr(year, month, longestStart);
      const toStr = toDateStr(year, month, longestStart + longestRun - 1);
      found.push({
        kind: "empty_week",
        severity,
        label: `Empty stretch: ${fromStr} → ${toStr} (${longestRun} days)`,
        detail: "No deliverables scheduled — consider filling with evergreen posts.",
        itemIds: [], // no items to filter to; disabled click
      });
    }

    // Sort by severity then kind.
    const sev = { high: 0, medium: 1, low: 2 };
    found.sort((a, b) => sev[a.severity] - sev[b.severity]);
    return found;
  }, [items, year, month]);

  const totals = useMemo(() => {
    const total = (items || []).length;
    let delivered = 0, pending = 0, overdue = 0;
    const today = new Date().toISOString().slice(0, 10);
    for (const it of items || []) {
      if (it.status === "published") delivered += 1;
      else if (it.status !== "cancelled") pending += 1;
      if (it.planned_date && it.planned_date < today && it.status !== "published" && it.status !== "cancelled") {
        overdue += 1;
      }
    }
    return { total, delivered, pending, overdue };
  }, [items]);

  const askAI = async () => {
    if (!wsId) return;
    setLoadingAI(true);
    setNarrative("");
    try {
      const monthStr = `${year}-${String(month).padStart(2, "0")}`;
      const r = await api.post(`/workspaces/${wsId}/content-calendar/ai-insight`, {
        month: monthStr,
        workspace_name: workspaceName || "",
        total_items: totals.total,
        delivered: totals.delivered,
        pending: totals.pending,
        overdue: totals.overdue,
        heuristics: heuristics.map((h) => ({ kind: h.kind, severity: h.severity, label: h.label, detail: h.detail })),
      });
      setNarrative(r.data?.narrative || "No narrative returned.");
    } catch (e) {
      toast.error(apiErrorMessage(e, "AI narrative failed"));
    } finally {
      setLoadingAI(false);
    }
  };

  return (
    <div className="border border-border bg-muted/10" data-testid="cc-tl-insights">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/30"
        data-testid="cc-tl-insights-toggle">
        {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <Sparkles className="w-3.5 h-3.5 text-primary" />
        <span className="text-[11px] uppercase tracking-widest font-semibold">Timeline Insights</span>
        <span className="text-[10px] text-muted-foreground">
          {heuristics.length === 0 ? "on track" : `${heuristics.length} finding${heuristics.length === 1 ? "" : "s"}`}
        </span>
        <div className="ml-auto flex items-center gap-3 text-[10px] text-muted-foreground">
          <span>Total: <b>{totals.total}</b></span>
          <span>Delivered: <b className="text-emerald-700">{totals.delivered}</b></span>
          <span>Pending: <b className="text-amber-700">{totals.pending}</b></span>
          {totals.overdue > 0 && <span>Overdue: <b className="text-rose-600">{totals.overdue}</b></span>}
        </div>
      </button>
      {expanded && (
        <div className="px-3 py-2 border-t border-border/60 space-y-2">
          {heuristics.length === 0 && (
            <div className="text-[11px] text-emerald-700 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              No risks flagged this month — nice work.
            </div>
          )}
          {heuristics.length > 0 && (
            <ul className="grid gap-1.5 md:grid-cols-2">
              {heuristics.map((h, i) => {
                const meta = SEVERITY_META[h.severity] || SEVERITY_META.low;
                const clickable = onFilterChange && h.itemIds && h.itemIds.length > 0;
                const Wrapper = clickable ? "button" : "div";
                const wrapperProps = clickable ? {
                  onClick: () => onFilterChange({ kind: h.kind, itemIds: h.itemIds, label: h.label }),
                  className: "text-left flex items-start gap-2 text-[11px] w-full hover:bg-primary/10 rounded-sm px-1 -mx-1 cursor-pointer",
                  title: "Click to filter timeline",
                } : {
                  className: "flex items-start gap-2 text-[11px]",
                };
                return (
                  <li key={i} data-testid={`cc-tl-insight-${h.kind}-${i}`}>
                    <Wrapper {...wrapperProps} data-testid={clickable ? `cc-tl-insight-chip-${h.kind}-${i}` : undefined}>
                      <AlertTriangle className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${meta.text}`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                          <span className={`text-[9px] uppercase tracking-widest font-semibold ${meta.text}`}>{meta.label}</span>
                          <span className="font-semibold text-foreground">{h.label}</span>
                          {clickable && <span className="text-[9px] text-primary underline decoration-dotted underline-offset-2">Filter</span>}
                        </div>
                        {h.detail && <div className="text-[10px] text-muted-foreground ml-3.5 truncate">{h.detail}</div>}
                      </div>
                    </Wrapper>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="flex items-center gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              className="h-7 rounded-none text-[11px]"
              onClick={askAI}
              disabled={loadingAI}
              data-testid="cc-tl-insights-ai-btn">
              {loadingAI
                ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Thinking…</>
                : <><Sparkles className="w-3.5 h-3.5 mr-1" /> Ask AI for narrative</>}
            </Button>
            {narrative && (
              <span className="text-[10px] text-muted-foreground">Narrative refreshed</span>
            )}
          </div>
          {narrative && (
            <div className="mt-2 p-2 border border-primary/30 bg-primary/5 text-[11px] leading-relaxed whitespace-pre-wrap"
                 data-testid="cc-tl-insights-ai-narrative">
              {narrative}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
